import { readonly, shallowRef, type DeepReadonly, type Ref } from 'vue';

import type { PushNotificationCapability } from '../api/push-notifications-api';
import type { BrowserPushAdapter, BrowserPushSubscription, BrowserPushSupport } from './browser-push-adapter';
import type { PushSubscriptionOwnershipManager } from './push-subscription-ownership';

import { PushNotificationApiError } from '../api/push-notifications-api';
import { base64UrlToUint8Array, unsubscribeAndVerify } from './browser-push-adapter';

export type PushNotificationStatus =
  | 'unsupported'
  | 'server-disabled'
  | 'ios-requires-standalone'
  | 'default'
  | 'denied'
  | 'subscribing'
  | 'subscribed'
  | 'unsubscribing'
  | 'unclaimed'
  | 'cleanup-failed'
  | 'error';

export type PushNotificationErrorKind =
  | 'capability-unavailable'
  | 'invalid-public-key'
  | 'invalid-subscription'
  | 'session-unavailable'
  | 'subscription-failed'
  | 'server-sync-failed'
  | 'unsubscribe-failed';

export type EnablePushOutcome = 'subscribed' | 'denied' | 'dismissed' | 'unsupported' | 'error';
export type DisablePushOutcome = 'disabled' | 'server-cleanup-failed' | 'error';
export type TestPushOutcome = 'sent' | 'rate-limited' | 'not-subscribed' | 'error';
export type PushCleanupOutcome = 'cleaned' | 'not-subscribed' | 'server-cleanup-failed' | 'cleanup-failed';

export interface PushNotificationApi {
  getCapability(signal?: AbortSignal): Promise<PushNotificationCapability>;
  upsert(subscription: { endpoint: string; p256dh: string; auth: string }, signal?: AbortSignal): Promise<void>;
  delete(endpoint: string, signal?: AbortSignal): Promise<void>;
  test(endpoint: string, signal?: AbortSignal): Promise<void>;
}

export interface PushNotificationController {
  status: DeepReadonly<Ref<PushNotificationStatus>>;
  errorKind: DeepReadonly<Ref<PushNotificationErrorKind | null>>;
  isSubscribed: DeepReadonly<Ref<boolean>>;
  setUser(userId: string | null): void;
  reconcile(): Promise<void>;
  refreshLocalState(): Promise<void>;
  enable(): Promise<EnablePushOutcome>;
  disable(): Promise<DisablePushOutcome>;
  sendTest(): Promise<TestPushOutcome>;
  cleanupForLogout(): Promise<PushCleanupOutcome>;
  cleanupForSessionLoss(): Promise<PushCleanupOutcome>;
}

export interface PushNotificationSessionState {
  isImpersonating(): boolean;
}

interface OperationContext {
  userId: string;
  signal: AbortSignal;
}

const hasValidKeys = (
  subscription: BrowserPushSubscription,
): subscription is BrowserPushSubscription & { p256dh: string; auth: string } => {
  return subscription.endpoint.length > 0 && Boolean(subscription.p256dh) && Boolean(subscription.auth);
};

export const createPushNotificationController = (
  browser: BrowserPushAdapter,
  api: PushNotificationApi,
  ownership: PushSubscriptionOwnershipManager,
  sessionState: PushNotificationSessionState = {
    isImpersonating: () => false,
  },
): PushNotificationController => {
  const status = shallowRef<PushNotificationStatus>('default');
  const errorKind = shallowRef<PushNotificationErrorKind | null>(null);
  const isSubscribed = shallowRef(false);
  let currentUserId: string | null = null;
  let generationAbortController = new AbortController();
  let operationQueue = Promise.resolve();

  const enqueue = <T>(operation: () => Promise<T>): Promise<T> => {
    const result = operationQueue.then(operation, operation);
    operationQueue = result.then(
      () => undefined,
      () => undefined,
    );

    return result;
  };

  const setStatus = (nextStatus: PushNotificationStatus): void => {
    status.value = nextStatus;
    errorKind.value = null;
  };

  const setError = (kind: PushNotificationErrorKind): void => {
    errorKind.value = kind;
    status.value = 'error';
  };

  const setCleanupFailed = (): void => {
    errorKind.value = 'unsubscribe-failed';
    status.value = 'cleanup-failed';
  };

  const getBrowserReadyStatus = (support: BrowserPushSupport = browser.getSupport()): PushNotificationStatus => {
    if (support.gate === 'requires-ios-standalone') {
      return 'ios-requires-standalone';
    }

    if (support.gate === 'unsupported') {
      return 'unsupported';
    }

    return support.permission === 'denied' ? 'denied' : 'default';
  };

  const shortCircuitUnavailableBrowser = (support: BrowserPushSupport): boolean => {
    if (support.gate === 'ready') {
      return false;
    }

    isSubscribed.value = false;
    setStatus(getBrowserReadyStatus(support));
    return true;
  };

  const captureContext = (): OperationContext | null => {
    if (!currentUserId) {
      return null;
    }

    return {
      userId: currentUserId,
      signal: generationAbortController.signal,
    };
  };

  // setUser() всегда abort'ит контроллер перед сменой пользователя,
  // поэтому «контекст актуален» ⇔ «его signal не отменён».
  const isCurrent = (context: OperationContext): boolean => {
    return !context.signal.aborted;
  };

  const loadCapability = async (context: OperationContext): Promise<Uint8Array | null> => {
    const capability = await api.getCapability(context.signal);
    if (!isCurrent(context)) {
      return null;
    }

    if (!capability.available || !capability.enabled || !capability.publicKey) {
      setStatus('server-disabled');
      return null;
    }

    try {
      return base64UrlToUint8Array(capability.publicKey);
    } catch {
      setError('invalid-public-key');
      console.warn('[push.capability] Некорректная конфигурация публичного ключа', {
        kind: 'invalid-public-key',
        phase: 'decode',
      });
      return null;
    }
  };

  const tryRemoveOwnedServerSubscription = async (
    subscription: BrowserPushSubscription,
    context: OperationContext | null,
    phase: 'disable' | 'logout' | 'session-loss',
    logPrefix: string,
  ): Promise<boolean> => {
    if (
      !context ||
      (await ownership.classify(context.userId, subscription.endpoint)) !== 'owned' ||
      !isCurrent(context)
    ) {
      return true;
    }
    try {
      await api.delete(subscription.endpoint, context.signal);
      return true;
    } catch {
      console.warn(`${logPrefix} Не удалось удалить серверную подписку`, {
        kind: 'server-cleanup-failure',
        phase,
      });
      return false;
    }
  };

  const retireLocally = async (
    subscription: BrowserPushSubscription,
    phase: 'disable' | 'logout' | 'reconcile' | 'rollback' | 'session-loss',
    context: OperationContext | null,
  ): Promise<boolean> => {
    const canUpdateState = (): boolean => (context ? isCurrent(context) : currentUserId === null);
    const removed = await unsubscribeAndVerify(browser, subscription);
    if (!removed) {
      if (canUpdateState()) {
        isSubscribed.value = true;
        setCleanupFailed();
      }
      console.warn('[push.cleanup] Браузер сохранил локальную подписку', {
        kind: 'subscription-still-present',
        phase,
      });
      return false;
    }

    const markerCleared = ownership.clear();
    if (!markerCleared) {
      if (canUpdateState()) {
        isSubscribed.value = false;
        setCleanupFailed();
      }
      return false;
    }

    if (canUpdateState()) {
      isSubscribed.value = false;
    }

    return true;
  };

  const reconcile = (): Promise<void> =>
    enqueue(async () => {
      if (sessionState.isImpersonating()) {
        console.warn('[push.reconcile] Пропуск reconciliation: активна impersonation', {
          phase: 'reconcile',
        });
        return;
      }

      const context = captureContext();
      const support = browser.getSupport();
      const browserStatus = getBrowserReadyStatus(support);
      isSubscribed.value = false;
      setStatus(browserStatus);

      if (!context) {
        setError('session-unavailable');
        return;
      }

      if (support.gate !== 'ready' || browserStatus !== 'default') {
        return;
      }

      const capability = await loadCapability(context);
      if (!capability || !isCurrent(context)) {
        return;
      }

      let subscription: BrowserPushSubscription | null;
      try {
        subscription = await browser.getSubscription();
      } catch {
        if (isCurrent(context)) {
          setError('subscription-failed');
        }
        console.warn('[push.reconcile] Не удалось прочитать локальную подписку', {
          kind: 'browser-read-failure',
          phase: 'reconcile',
        });
        return;
      }

      if (!isCurrent(context)) {
        return;
      }

      if (!subscription) {
        ownership.clear();
        setStatus('default');
        return;
      }

      const owner = await ownership.classify(context.userId, subscription.endpoint);
      if (!isCurrent(context)) {
        return;
      }

      if (owner !== 'owned') {
        isSubscribed.value = true;
        if (await retireLocally(subscription, 'reconcile', context)) {
          setStatus('unclaimed');
        }
        return;
      }

      if (!hasValidKeys(subscription)) {
        isSubscribed.value = true;
        setError('invalid-subscription');
        console.warn('[push.reconcile] Локальная подписка не содержит обязательные ключи', {
          kind: 'invalid-subscription',
          phase: 'reconcile',
        });
        return;
      }

      try {
        await api.upsert(
          {
            endpoint: subscription.endpoint,
            p256dh: subscription.p256dh,
            auth: subscription.auth,
          },
          context.signal,
        );
        if (!isCurrent(context)) {
          return;
        }

        isSubscribed.value = true;
        setStatus('subscribed');
      } catch {
        if (isCurrent(context)) {
          isSubscribed.value = true;
          setError('server-sync-failed');
        }
        console.warn('[push.reconcile] Не удалось синхронизировать существующую подписку', {
          kind: 'server-sync-failure',
          phase: 'reconcile',
        });
      }
    });

  const refreshLocalState = (): Promise<void> =>
    enqueue(async () => {
      const context = captureContext();
      const support = browser.getSupport();
      if (shortCircuitUnavailableBrowser(support)) {
        return;
      }

      let subscription: BrowserPushSubscription | null;
      try {
        subscription = await browser.getSubscription();
      } catch {
        console.warn('[push.refresh] Не удалось прочитать локальную подписку', {
          kind: 'browser-read-failure',
          phase: 'refresh',
        });
        return;
      }

      if (context && !isCurrent(context)) {
        return;
      }

      if (!subscription) {
        ownership.clear();
        isSubscribed.value = false;
        setStatus(getBrowserReadyStatus(support));
        return;
      }

      isSubscribed.value = true;
      if (!context || (await ownership.classify(context.userId, subscription.endpoint)) !== 'owned') {
        setStatus('unclaimed');
        return;
      }

      if (isCurrent(context)) {
        setStatus('subscribed');
      }
    });

  const enable = (): Promise<EnablePushOutcome> =>
    enqueue(async () => {
      const context = captureContext();
      if (!context) {
        setError('session-unavailable');
        return 'error';
      }

      const support = browser.getSupport();
      const browserStatus = getBrowserReadyStatus(support);
      if (support.gate !== 'ready') {
        shortCircuitUnavailableBrowser(support);
        setStatus(browserStatus);
        return 'unsupported';
      }

      if (browserStatus === 'denied') {
        setStatus('denied');
        return 'denied';
      }

      const capability = await loadCapability(context);
      if (!capability || !isCurrent(context)) {
        return status.value === 'server-disabled' ? 'unsupported' : 'error';
      }

      let permission = support.permission;
      if (permission === 'default') {
        try {
          permission = await browser.requestPermission();
        } catch {
          if (isCurrent(context)) {
            setError('subscription-failed');
          }
          console.warn('[push.permission] Не удалось запросить разрешение браузера', {
            kind: 'permission-failure',
            phase: 'enable',
          });
          return 'error';
        }
      }

      if (!isCurrent(context)) {
        return 'error';
      }

      if (permission === 'denied') {
        setStatus('denied');
        return 'denied';
      }

      if (permission !== 'granted') {
        setStatus('default');
        return 'dismissed';
      }

      setStatus('subscribing');
      let subscription: BrowserPushSubscription | null = null;
      let createdLocally = false;

      try {
        subscription = await browser.getSubscription();
        if (subscription) {
          const owner = await ownership.classify(context.userId, subscription.endpoint);
          if (!isCurrent(context)) {
            return 'error';
          }

          if (owner !== 'owned') {
            if (!(await retireLocally(subscription, 'reconcile', context))) {
              return 'error';
            }
            subscription = null;
          }
        }

        if (!subscription) {
          subscription = await browser.subscribe(capability);
          createdLocally = true;
        }

        if (!isCurrent(context)) {
          if (createdLocally) {
            await retireLocally(subscription, 'rollback', null);
          }
          return 'error';
        }

        if (!hasValidKeys(subscription)) {
          if (createdLocally) {
            await retireLocally(subscription, 'rollback', context);
          }
          if (isCurrent(context) && status.value !== 'cleanup-failed') {
            setError('invalid-subscription');
          }
          console.warn('[push.subscribe] Браузер вернул подписку без обязательных ключей', {
            kind: 'invalid-subscription',
            phase: 'enable',
          });
          return 'error';
        }

        const ownerBeforeMutation = await ownership.classify(context.userId, subscription.endpoint);
        if (
          !isCurrent(context) ||
          (!createdLocally && ownerBeforeMutation !== 'owned') ||
          ownerBeforeMutation === 'foreign'
        ) {
          if (createdLocally) {
            await retireLocally(subscription, 'rollback', context);
          }
          return 'error';
        }

        await api.upsert(
          {
            endpoint: subscription.endpoint,
            p256dh: subscription.p256dh,
            auth: subscription.auth,
          },
          context.signal,
        );
        if (!isCurrent(context)) {
          if (createdLocally) {
            await retireLocally(subscription, 'rollback', null);
          }
          return 'error';
        }

        if (!(await ownership.claim(context.userId, subscription.endpoint))) {
          if (createdLocally) {
            try {
              await api.delete(subscription.endpoint, context.signal);
            } catch {
              console.warn('[push.subscribe] Не удалось откатить серверную подписку', {
                kind: 'server-cleanup-failure',
                phase: 'rollback',
              });
            }
            await retireLocally(subscription, 'rollback', context);
          }
          if (status.value !== 'cleanup-failed') {
            setError('server-sync-failed');
          }
          return 'error';
        }

        isSubscribed.value = true;
        setStatus('subscribed');
        return 'subscribed';
      } catch {
        if (subscription && createdLocally) {
          await retireLocally(subscription, 'rollback', context);
        }
        if (isCurrent(context) && status.value !== 'cleanup-failed') {
          setError(subscription ? 'server-sync-failed' : 'subscription-failed');
        }
        console.warn('[push.subscribe] Не удалось включить push-уведомления', {
          kind: subscription ? 'server-sync-failure' : 'subscription-failure',
          phase: 'enable',
        });
        return 'error';
      }
    });

  const disable = (): Promise<DisablePushOutcome> =>
    enqueue(async () => {
      const context = captureContext();
      const support = browser.getSupport();
      if (shortCircuitUnavailableBrowser(support)) {
        return 'disabled';
      }

      let subscription: BrowserPushSubscription | null;
      try {
        subscription = await browser.getSubscription();
      } catch {
        setCleanupFailed();
        console.warn('[push.unsubscribe] Не удалось прочитать локальную подписку', {
          kind: 'browser-read-failure',
          phase: 'disable',
        });
        return 'error';
      }

      if (!subscription) {
        ownership.clear();
        isSubscribed.value = false;
        setStatus(getBrowserReadyStatus(support));
        return 'disabled';
      }

      setStatus('unsubscribing');
      const serverCleanupFailed = !(await tryRemoveOwnedServerSubscription(
        subscription,
        context,
        'disable',
        '[push.unsubscribe]',
      ));

      if (!(await retireLocally(subscription, 'disable', context))) {
        return 'error';
      }

      if (context && !isCurrent(context)) {
        return 'error';
      }

      if (serverCleanupFailed) {
        setError('server-sync-failed');
        return 'server-cleanup-failed';
      }

      setStatus(getBrowserReadyStatus(support));
      return 'disabled';
    });

  const sendTest = (): Promise<TestPushOutcome> =>
    enqueue(async () => {
      const context = captureContext();
      if (!context) {
        return 'error';
      }

      const support = browser.getSupport();
      if (shortCircuitUnavailableBrowser(support)) {
        return 'not-subscribed';
      }

      let subscription: BrowserPushSubscription | null;
      try {
        subscription = await browser.getSubscription();
      } catch {
        return 'error';
      }

      if (!subscription) {
        ownership.clear();
        isSubscribed.value = false;
        setStatus(getBrowserReadyStatus(support));
        return 'not-subscribed';
      }

      if ((await ownership.classify(context.userId, subscription.endpoint)) !== 'owned' || !isCurrent(context)) {
        isSubscribed.value = true;
        setStatus('unclaimed');
        return 'not-subscribed';
      }

      try {
        await api.test(subscription.endpoint, context.signal);
        return isCurrent(context) ? 'sent' : 'error';
      } catch (error) {
        if (error instanceof PushNotificationApiError && error.kind === 'rate-limited' && isCurrent(context)) {
          return 'rate-limited';
        }

        console.warn('[push.test] Не удалось отправить тестовое уведомление', {
          kind: 'delivery-failure',
          phase: 'test',
        });
        return 'error';
      }
    });

  const cleanup = (serverFirst: boolean, phase: 'logout' | 'session-loss'): Promise<PushCleanupOutcome> =>
    enqueue(async () => {
      const context = captureContext();
      const support = browser.getSupport();
      if (shortCircuitUnavailableBrowser(support)) {
        return 'not-subscribed';
      }

      let subscription: BrowserPushSubscription | null;
      try {
        subscription = await browser.getSubscription();
      } catch {
        setCleanupFailed();
        console.warn('[push.cleanup] Не удалось прочитать локальную подписку', {
          kind: 'browser-read-failure',
          phase,
        });
        return 'cleanup-failed';
      }

      if (!subscription) {
        ownership.clear();
        isSubscribed.value = false;
        setStatus(getBrowserReadyStatus(support));
        return 'not-subscribed';
      }

      const serverCleanupFailed =
        serverFirst && !(await tryRemoveOwnedServerSubscription(subscription, context, phase, '[push.cleanup]'));

      if (!(await retireLocally(subscription, phase, context))) {
        return 'cleanup-failed';
      }

      if (context ? isCurrent(context) : currentUserId === null) {
        setStatus(getBrowserReadyStatus(support));
      }

      return serverCleanupFailed ? 'server-cleanup-failed' : 'cleaned';
    });

  const setUser = (userId: string | null): void => {
    if (currentUserId === userId) {
      return;
    }

    generationAbortController.abort();
    generationAbortController = new AbortController();
    currentUserId = userId;
    isSubscribed.value = false;
    setStatus(getBrowserReadyStatus());
  };

  return {
    status: readonly(status),
    errorKind: readonly(errorKind),
    isSubscribed: readonly(isSubscribed),
    setUser,
    reconcile,
    refreshLocalState,
    enable,
    disable,
    sendTest,
    cleanupForLogout: () => cleanup(true, 'logout'),
    cleanupForSessionLoss: () => cleanup(false, 'session-loss'),
  };
};
